import { useMemo, useState } from 'react'
import styles from './charts.module.scss'

const CHART_WIDTH = 600
const CHART_HEIGHT = 220
const PADDING = { top: 10, right: 10, bottom: 28, left: 40 }
const MAX_X_LABELS = 6

const initialHoverState = { active: false, seriesKey: null, date: null, count: null }

const pickLabelIndexes = total => {
  if (total <= MAX_X_LABELS) return Array.from({ length: total }, (_, i) => i)
  const indexes = []
  for (let i = 0; i < MAX_X_LABELS; i++) {
    indexes.push(Math.round((i * (total - 1)) / (MAX_X_LABELS - 1)))
  }
  return [...new Set(indexes)]
}

const formatAxisDate = (dateString, rangeType = 'weeks') => {
  if (!dateString) return ''

  const [year, month, day] = dateString.split('-')
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))

  if (rangeType === 'months') {
    return new Intl.DateTimeFormat('es-ES', { month: 'short', year: 'numeric' }).format(date)
  }

  return `${day}/${month}/${year}`
}

// POC: dependency-free SVG line chart. Replace with a chart library
// (recharts / @ant-design/charts) when the real implementation lands.
const LineChart = ({ series, rangeType = 'weeks', highlightedSeries = null }) => {
  const [hover, setHover] = useState(initialHoverState)
  const allPoints = useMemo(() => series.flatMap(s => s.points), [series])
  const dates = useMemo(() => [...new Set(allPoints.map(p => p.date))].sort(), [allPoints])
  const hasData = series.some(s => s.points.length > 0)
  if (!hasData) return null

  const maxCount = Math.max(...allPoints.map(p => p.count), 1)
  const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right
  const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom

  const xFor = date => {
    const index = dates.indexOf(date)
    return PADDING.left + (dates.length > 1 ? (index / (dates.length - 1)) * innerWidth : innerWidth / 2)
  }
  const yFor = count => PADDING.top + innerHeight - (count / maxCount) * innerHeight

  return (
    <div className={styles.chart_wrapper}>
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className={styles.chart_svg}
        role='img'
      >
        {[0, 0.5, 1].map(fraction => {
          const y = PADDING.top + innerHeight - fraction * innerHeight
          return (
            <g key={fraction}>
              <line x1={PADDING.left} y1={y} x2={CHART_WIDTH - PADDING.right} y2={y} className={styles.grid_line} />
            </g>
          )
        })}
        {series.map(s => {
          const isHighlighted = highlightedSeries === s.key
          const isDimmed = highlightedSeries && highlightedSeries !== s.key
          return (
            <g key={s.key} style={{ opacity: isDimmed ? 0.2 : 1, transition: 'opacity 0.3s' }}>
              <polyline
                fill='none'
                stroke={s.color}
                strokeWidth={isHighlighted ? 3.5 : 2}
                points={s.points.map(p => `${xFor(p.date)},${yFor(p.count)}`).join(' ')}
              />
              {s.points.map(point => {
                const isActive = hover.active && hover.seriesKey === s.key && hover.date === point.date
                return (
                  <g key={`${s.key}-${point.date}`}>
                    <circle
                      cx={xFor(point.date)}
                      cy={yFor(point.count)}
                      r={isActive ? 5 : isHighlighted ? 4 : 3}
                      fill={s.color}
                      stroke='#fff'
                      strokeWidth='1.5'
                      data-testid={`chart-point-${s.key}-${point.date}`}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHover({ active: true, seriesKey: s.key, date: point.date, count: point.count })}
                      onMouseLeave={() => setHover(initialHoverState)}
                    />
                    <text
                      x={xFor(point.date)}
                      y={yFor(point.count) - 8}
                      textAnchor='middle'
                      className={styles.axis_label}
                      style={{ fontSize: '9px', fontWeight: isHighlighted ? 600 : 400 }}
                    >
                      {point.count}
                    </text>
                  </g>
                )
              })}
            </g>
          )
        })}
        {pickLabelIndexes(dates.length).map(index => {
          const isFirst = index === 0
          const isLast = index === dates.length - 1
          return (
            <text
              key={dates[index]}
              x={xFor(dates[index])}
              y={CHART_HEIGHT - 8}
              textAnchor={isFirst ? 'start' : isLast ? 'end' : 'middle'}
              className={styles.axis_label}
            >
              {formatAxisDate(dates[index], rangeType)}
            </text>
          )
        })}
      </svg>

      {hover.active && (
        <div className={styles.tooltip} data-testid='chart-tooltip'>
          <div className={styles.tooltip_date}>{hover.date}</div>
          <div className={styles.tooltip_row}>
            <span className={styles.tooltip_label}>
              <span className={styles.tooltip_dot} style={{ backgroundColor: series.find(s => s.key === hover.seriesKey)?.color || '#000' }} />
              {series.find(s => s.key === hover.seriesKey)?.label}
            </span>
            <strong>{hover.count}</strong>
          </div>
        </div>
      )}

      <div className={styles.legend}>
        {series.map(s => {
          const isHighlighted = highlightedSeries === s.key
          const isDimmed = highlightedSeries && highlightedSeries !== s.key
          return (
            <span
              key={s.key}
              className={styles.legend_item}
              style={{
                opacity: isDimmed ? 0.35 : 1,
                fontWeight: isHighlighted ? 700 : 400,
                transition: 'opacity 0.3s, font-weight 0.3s'
              }}
            >
              <span className={styles.legend_swatch} style={{ backgroundColor: s.color }} />
              {s.label}
            </span>
          )
        })}
      </div>
    </div>
  )
}

export default LineChart
